// Employee portal — DESIGN §6.2 / §6.2.1.
//
// Anti-anthropomorphism (DESIGN §1.4 #8) is a foreseeable-harm liability
// defence, not a tone preference. The copy here is deliberately neutral and
// factual — no first-person plural ("we"), no availability promise ("here
// when you need us"), no personalised emotional address.
//
// ONE ROUTE TREE, TWO CHROMES BY VIEWPORT (PO 2026-07-31). `/portal/*` is a
// nested route tree; `PortalShell` renders the dark section rail at `lg`+ and
// the bottom-nav app shell below it. Every section is a real URL, so both
// chromes drive the same `<Outlet/>` and nothing is reachable from only one.
//
// WHAT THIS REPLACED — there were THREE Employee portals:
//   1. `/portal` at `lg`+ — the section sidebar, sections in `useState`.
//   2. `/portal` below `lg` — a single long anchored stack (`MobileDashboard`),
//      which is what a phone actually got on any reload or deep-link.
//   3. `/portal/mobile/*` — the bottom-nav shell, entered ONLY by a User-Agent
//      match at the sign-in landing redirect, so almost nothing reached it.
// (3) was the approved design and the one users saw least. Worse, it silently
// dropped content (2) carried: the §14 privacy summary — which §3.7 makes
// load-bearing, the employee has to be able to see what HR and managers can
// see — plus support preferences, the magic-link reissue, and sign-out. The
// "More" tab exists to make sure a phone can still reach all of it.
//
// Sections became URLs rather than `useState` for the same reason the Manager
// tree did: a viewport branch swaps component trees, so anything held in
// component state is destroyed when a phone rotates past the breakpoint. The
// Companion transcript and its red-flag lock are protected separately and more
// strongly, in the query cache — see `lib/use-companion-chat.ts`.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import type {
  EmployeeEntitlements,
  EmployeePortal as EmployeePortalResponse,
  EmployeeRtwPlan,
} from '@besideu/shared/cases';
import { api } from '@/lib/api.ts';
import { useEmployeePortal } from '@/lib/use-employee-portal.ts';
import { useRecordResourceAccess } from '@/lib/resource-access.ts';
import { useSession } from '@/lib/auth.ts';
import { firstNameFromSession } from '@/lib/format.ts';
import { Button } from '@/components/ui/Button.tsx';
import { NavCard } from '@/components/ui/Card.tsx';
import type { NavItem } from '@/components/AppNav.tsx';
import type { MobileTab } from '@/components/MobileTabBar.tsx';
import { Shell, useSignOut, FULL_BLEED_CONTENT_CLASSNAME } from '@/components/Shell.tsx';
import { MaeveGreetingHero } from '@/components/MaeveGreetingHero.tsx';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import {
  HomeIcon,
  CalendarIcon,
  DocIcon,
  SlidersIcon,
  ChatIcon,
  BookIcon,
  ShieldIcon,
  BarsIcon,
} from '@/components/ui/icons.tsx';
import { EmployeeMaeveFirst } from '@/components/EmployeeMaeveFirst.tsx';
import { EntitlementsSummary } from '@/components/EntitlementsSummary.tsx';
import { EmployeeCompanionChat } from '@/components/EmployeeCompanionChat.tsx';
import { SupportPreferencesCard } from '@/components/SupportPreferencesCard.tsx';
import { EmployeeResourcesCard } from '@/components/EmployeeResourcesCard.tsx';
import { SupportPathwayView } from '@/components/SupportPathwayView.tsx';
import { PrivacySummary } from '@/components/PrivacySummary.tsx';
import { MagicLinkReissueCard } from '@/components/MagicLinkReissueCard.tsx';
import { TAILWIND_LG_MAX_WIDTH_QUERY, useMediaQuery } from '@/lib/use-media-query.ts';

// Employee Portal & App spec §20 — recommended portal navigation, now as
// routes. The rail (lg+) carries all seven; the bottom tab bar carries four
// and "More" holds the rest (see `PortalMore`).
const PORTAL_NAV: NavItem[] = [
  { to: '/portal', label: 'My overview', end: true, icon: <HomeIcon /> },
  { to: '/portal/check-ins', label: 'My check-ins', icon: <CalendarIcon /> },
  { to: '/portal/leave', label: 'Leave and entitlements', icon: <DocIcon /> },
  { to: '/portal/preferences', label: 'Preferences', icon: <SlidersIcon /> },
  { to: '/portal/maeve', label: 'Talk to Maeve', icon: <ChatIcon /> },
  { to: '/portal/resources', label: 'Wellbeing resources', icon: <BookIcon /> },
  { to: '/portal/privacy', label: 'Privacy', icon: <ShieldIcon /> },
];

const PORTAL_TABS: MobileTab[] = [
  { to: '/portal', label: 'Home', icon: <HomeIcon size={22} />, end: true },
  // Warm variant + Maeve's face as the tab icon — matches the Companion
  // chat's own branding rather than a generic chat glyph.
  {
    to: '/portal/maeve',
    label: 'Maeve',
    icon: <MaeveAvatar size={22} variant="warm" rings={false} />,
  },
  { to: '/portal/check-ins', label: 'Check-ins', icon: <CalendarIcon size={22} /> },
  { to: '/portal/resources', label: 'Resources', icon: <BookIcon size={22} /> },
  { to: '/portal/more', label: 'More', icon: <BarsIcon size={22} /> },
];

// The one page that paints edge-to-edge inside the app shell: the Companion
// chat owns the full viewport between header and tab bar and scrolls
// internally, matching Manager's `/manager/maeve` — see `ManagerShell.tsx`'s
// identical `FULL_BLEED_PATHS` for the reasoning. `PortalShell`'s breakpoint
// is `lg`, not `md`, so this switches back to a bounded column at `lg`, same
// as the tab bar giving way to the sidebar (no intermediate range to worry
// about, unlike Manager/HR's `md` breakpoint vs. the sidebar's own `lg:flex`
// — see the Manager/HR account-block fix this mirrors).
const FULL_BLEED_PATHS = new Set(['/portal/maeve']);

export function PortalShell() {
  // No JS branch — `Shell` renders one element tree and toggles chrome with
  // breakpoint classes. See its header: branching here unmounted the whole
  // page on a rotation, wiping unsaved preference selections and a
  // part-written Companion message along with it.
  //
  // `breakpoint="lg"`, not `md`: the dark rail only renders at lg+, so below
  // it a tablet gets the soft conversational front door rather than a
  // nav-less gap. `surface="employee"` opts into the §2 blue palette —
  // `data-surface` re-points the primary tokens (see index.css) and every
  // teal affordance re-tints to Employee Blue.
  const { pathname } = useLocation();
  const fullBleed = FULL_BLEED_PATHS.has(pathname.replace(/\/$/, ''));

  return (
    <Shell
      chipLabel="My support"
      nav={PORTAL_NAV}
      routeId="employee-portal"
      tabs={PORTAL_TABS}
      tabsLabel="Employee"
      breakpoint="lg"
      surface="employee"
      contentClassName={
        fullBleed
          ? FULL_BLEED_CONTENT_CLASSNAME.lg
          : 'mx-auto w-full max-w-3xl px-4 py-5 lg:px-8 lg:py-10'
      }
    />
  );
}

// Every section route loads the portal through the same shared query key, so
// this is one fetch regardless of how many mount — prop-drilling `data` from
// the shell would have meant threading it through two different chromes.
function usePortalData() {
  const portal = useEmployeePortal();
  const session = useSession('employee-portal');
  return {
    ...portal,
    firstName: firstNameFromSession(session as { name?: string }),
  };
}

/** Shared loading/error framing so every section fails the same calm way. */
function SectionFrame({
  portal,
  loadingErrorClassName = '',
  children,
}: {
  portal: ReturnType<typeof usePortalData>;
  /**
   * Extra classes on the loading/error `<p>` only. Every other caller sits
   * inside `PortalShell`'s own padded content area and leaves this empty;
   * `PortalMaeve` is full-bleed (no shell padding of its own below `lg`) and
   * passes `p-6` so its loading/error text isn't flush against the edge.
   */
  loadingErrorClassName?: string;
  children: (data: EmployeePortalResponse) => React.ReactNode;
}) {
  if (portal.isPending) {
    return <p className={`text-muted-foreground ${loadingErrorClassName}`}>Loading…</p>;
  }
  if (portal.error || !portal.data) {
    return (
      <p className={`text-traffic-red ${loadingErrorClassName}`}>
        Could not load your portal. Try refreshing.
      </p>
    );
  }
  return <>{children(portal.data)}</>;
}

// §9.2.1 — the portal Home: a calm welcome, the one thing that may need
// attention (the RTW plan, when present), and quiet links onward. Not a
// dashboard, not a task list.
//
// Below `lg` it leads with the Maeve-First entry (PO Spec §1.5) — the soft
// conversational front door a bereaved employee should meet first — and the
// "where to go" grid is redundant there because the tab bar is doing that job.
export function PortalHome() {
  const portal = usePortalData();
  const isCompact = useMediaQuery(TAILWIND_LG_MAX_WIDTH_QUERY);

  return (
    <SectionFrame portal={portal}>
      {(data) => (
        <div className="space-y-6">
          {isCompact ? (
            <EmployeeMaeveFirst data={data} firstName={portal.firstName} />
          ) : (
            /* Greeting hero. The first-person voice + Maeve's face are
               CGC-approved (2026-07-11, DESIGN §1.4 #8 / §6.2.1), so the face
               always renders. The copy still personalises only when the JWT
               `name` claim is present — graceful degradation, not a gate. */
            <MaeveGreetingHero
              variant="warm"
              title={
                portal.firstName
                  ? `You don't have to figure this out alone, ${portal.firstName}.`
                  : 'Your support space'
              }
              subtitle={
                portal.firstName
                  ? 'Everything here is available to you whenever you need it, for as long as you need it. There is nothing you need to do.'
                  : 'A private place to understand the support available to you, set how you would like to be contacted, and find resources — whenever you feel ready. You do not need to do anything here.'
              }
            />
          )}

          {data.case && (
            <p className="text-sm text-muted-foreground">
              Your manager is{' '}
              <span className="font-medium text-foreground">{data.case.managerName}</span>.
            </p>
          )}

          {data.case && <EmployeeRtwPlanCard caseId={data.case.id} rtwPlan={data.rtwPlan} />}

          {isCompact ? null : (
            <section>
              <h2 className="mb-3 text-sm font-medium text-muted-foreground">Where to go</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <NavCard
                  to="/portal/check-ins"
                  icon={<CalendarIcon />}
                  title="My check-ins"
                  titleTone="foreground"
                  subtitle="The support moments your organisation has planned."
                />
                <NavCard
                  to="/portal/leave"
                  icon={<DocIcon />}
                  title="Leave and entitlements"
                  titleTone="foreground"
                  subtitle="What your organisation provides, in plain English."
                />
                <NavCard
                  to="/portal/preferences"
                  icon={<SlidersIcon />}
                  title="Preferences"
                  titleTone="foreground"
                  subtitle="How you would like to be contacted."
                />
                <NavCard
                  to="/portal/resources"
                  icon={<BookIcon />}
                  title="Wellbeing resources"
                  titleTone="foreground"
                  subtitle="Support inside and outside work."
                />
              </div>
            </section>
          )}
        </div>
      )}
    </SectionFrame>
  );
}

export function PortalCheckIns() {
  const portal = usePortalData();
  const navigate = useNavigate();

  return (
    <SectionFrame portal={portal}>
      {(data) => (
        <SupportPathwayView
          touchpoints={data.touchpoints}
          hasCase={!!data.case}
          // §15 opt-in "talk to Maeve about this moment". The moment LABEL
          // goes in the URL and the Maeve route composes the opener from it —
          // rather than putting the composed sentence in the URL, which would
          // read as words the employee never chose to publish. It only ever
          // seeds the composer; nothing is sent until they send it.
          onPrepareConversation={(momentLabel) =>
            void navigate(`/portal/maeve?moment=${encodeURIComponent(momentLabel)}`)
          }
        />
      )}
    </SectionFrame>
  );
}

export function PortalLeave() {
  const portal = usePortalData();
  return (
    <SectionFrame portal={portal}>
      {(data) =>
        data.case ? (
          <Entitlements caseId={data.case.id} entitlements={data.entitlements} />
        ) : (
          <EmptyLeave />
        )
      }
    </SectionFrame>
  );
}

export function PortalPreferences() {
  const portal = usePortalData();
  return (
    <SectionFrame portal={portal}>
      {(data) => <SupportPreferencesCard preferences={data.preferences} />}
    </SectionFrame>
  );
}

export function PortalMaeve() {
  const portal = usePortalData();
  const [searchParams] = useSearchParams();
  const moment = searchParams.get('moment');
  const prefill = moment ? `I’d like to talk about my "${moment}" support moment.` : undefined;

  // `h-full` below `lg`: `PortalShell` gives this route a full-bleed,
  // non-scrolling content wrapper, itself a flex child with a definite
  // height, so the chat fills it exactly and scrolls internally. At `lg`+ it
  // becomes a bounded column — `dvh` (not `vh`) so mobile-browser URL-bar
  // collapse can't leave the composer under the fold. Mirrors
  // `ManagerAskMaeve.tsx`'s identical wrapper (there, `md`; here, `lg`, since
  // that's `PortalShell`'s breakpoint). `loadingErrorClassName="p-6"`: every
  // other `SectionFrame` caller sits inside the shell's own padded content
  // area and needs none; this route is full-bleed below `lg` and has none of
  // its own, so its loading/error text would otherwise sit flush against the
  // edge.
  return (
    <div className="h-full overflow-hidden lg:h-[70dvh] lg:min-h-[26rem] lg:rounded-lg lg:border lg:border-border">
      <SectionFrame portal={portal} loadingErrorClassName="p-6">
        {(data) =>
          data.case ? (
            <EmployeeCompanionChat caseId={data.case.id} prefill={prefill} />
          ) : (
            <EmployeeCompanionChat prefill={prefill} />
          )
        }
      </SectionFrame>
    </div>
  );
}

export function PortalResources() {
  return <EmployeeResourcesCard />;
}

export function PortalPrivacy() {
  return (
    <div className="space-y-6">
      <PrivacySummary />
      <MagicLinkReissueCard />
    </div>
  );
}

// The "More" tab (below `lg` only — the rail carries these directly at lg+).
// This is not a nice-to-have: without it a phone loses the §14 privacy
// summary, support preferences, leave/entitlements, the magic-link reissue,
// and sign-out, all of which the pre-bottom-nav mobile stack carried. The
// §3.7 privacy summary is the load-bearing one.
export function PortalMore() {
  const logout = useSignOut();

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold">More</h1>
      <ul className="space-y-3">
        <li>
          <NavCard
            to="/portal/leave"
            icon={<DocIcon />}
            title="Leave and entitlements"
            titleTone="foreground"
            subtitle="What your organisation provides, in plain English."
          />
        </li>
        <li>
          <NavCard
            to="/portal/preferences"
            icon={<SlidersIcon />}
            title="Preferences"
            titleTone="foreground"
            subtitle="How you would like to be contacted."
          />
        </li>
        <li>
          <NavCard
            to="/portal/privacy"
            icon={<ShieldIcon />}
            title="Privacy"
            titleTone="foreground"
            subtitle="What your manager and HR can and cannot see."
          />
        </li>
      </ul>

      <div className="mt-8 border-t border-border pt-4 lg:hidden">
        <Button variant="ghost" onClick={() => logout.mutate()} loading={logout.isPending}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

function EmptyLeave() {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-6 text-sm text-muted-foreground">
      <p className="font-medium text-foreground">Your entitlements</p>
      <p className="mt-1">
        Leave and entitlement details will appear here once your organisation connects a case. Until
        then, your manager and HR team can answer questions.
      </p>
    </div>
  );
}

// Slice 18 — Return-to-Work plan panel for the Employee portal.
//
// Three states:
//   1. `rtwPlan === null` — no plan submitted yet by the Manager. Render
//      nothing (don't pre-announce a plan that hasn't been written).
//   2. `acknowledgedAt === null` — plan submitted, awaiting acknowledgement.
//      Show the plan details (sans `managerPrivateNotes`, which is
//      structurally absent from `EmployeeRtwPlan`) + an "Acknowledge"
//      button that calls the ack endpoint.
//   3. `acknowledgedAt !== null` — plan acknowledged. Read-only summary
//      with the ack timestamp.
//
// Anti-anthropomorphism (DESIGN §1.4 #8): neutral framing — the panel
// describes what the Manager has set up, doesn't promise emotional support.
function EmployeeRtwPlanCard({
  caseId,
  rtwPlan,
}: {
  caseId: string;
  rtwPlan: EmployeeRtwPlan | null;
}) {
  const queryClient = useQueryClient();
  const acknowledge = useMutation({
    mutationFn: () =>
      api.post<{ rtwPlan: EmployeeRtwPlan; alreadyAcknowledged: boolean }>(
        `/api/employee/rtw-plan/${caseId}/acknowledge`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['employee', 'portal'] }),
  });

  if (!rtwPlan) return null;
  const acked = rtwPlan.acknowledgedAt !== null;

  return (
    <div
      id="rtw-plan"
      className="scroll-mt-24 space-y-4 rounded-lg border border-border p-6 text-sm"
    >
      <header className="space-y-1">
        <p className="text-base font-medium">Your return-to-work plan</p>
        <p className="text-muted-foreground">
          Your manager has shared a plan for your return. Read it and acknowledge so the date is
          confirmed.
        </p>
      </header>

      <dl className="space-y-3">
        <PortalField label="Workload expectations">{rtwPlan.workloadExpectations}</PortalField>
        <PortalField label="Phased return">
          {phasedReturnLabel(rtwPlan.phasedReturn)}
          {rtwPlan.targetFullLoadDate ? ` — target full load by ${rtwPlan.targetFullLoadDate}` : ''}
        </PortalField>
        <PortalField label="Support">{rtwPlan.supportPreferences}</PortalField>
      </dl>

      {acked ? (
        <p className="rounded-md border border-traffic-green/30 bg-traffic-green/10 p-3 text-traffic-green-text">
          You acknowledged this plan on{' '}
          <time dateTime={rtwPlan.acknowledgedAt!}>
            {new Date(rtwPlan.acknowledgedAt!).toLocaleString('en-GB')}
          </time>
          .
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            loading={acknowledge.isPending}
            onClick={() => acknowledge.mutate()}
          >
            Acknowledge this plan
          </Button>
          {acknowledge.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              Could not record your acknowledgement. Try again or contact your manager.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PortalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}

function phasedReturnLabel(p: EmployeeRtwPlan['phasedReturn']): string {
  switch (p) {
    case 'Full_Time':
      return 'Full time from day one';
    case 'Part_Time':
      return 'Reduced hours';
    case 'Staggered':
      return 'Staggered ramp';
  }
}

// Slice 17 — RAG-grounded entitlements panel. Thin Employee-portal wrapper
// over the shared `EntitlementsSummary` (also used by the Manager case
// policy view) — this layer only owns the "Resources Accessed" instrumentation.
function Entitlements({
  caseId,
  entitlements,
}: {
  caseId: string;
  entitlements: EmployeeEntitlements | null;
}) {
  // PO Spec Phase 4 §3 (2026-06-21) — "Resources Accessed". The entitlements
  // panel renders RAG-grounded policy excerpts; surfacing them to the Employee
  // is a `policy_chunk_read`. Fire once per mount, and only when there is real
  // policy grounding (≥1 citation) — a placeholder/ungrounded summary isn't a
  // policy read. Fire-and-forget; no resourceRef so no filename crosses the
  // wire (privacy: family only, never user-facing copy).
  const recordResourceAccess = useRecordResourceAccess(caseId);
  return (
    <EntitlementsSummary
      id="entitlements"
      title="Your entitlements"
      placeholder="Specific leave and pay details will appear here once your organisation's policy documents are connected. Until then, your manager and HR team can answer questions."
      entitlements={entitlements}
      onGrounded={() => recordResourceAccess('policy_chunk_read')}
    />
  );
}
